import { Check, ChevronDown, Loader2 } from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { levenshteinDistance } from "src/common/utils";
import { Input } from "src/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "src/components/ui/popover";

import type { BuildkitePipeline } from "../types";

interface BuildkitePipelineTypeaheadProps {
  pipelines: BuildkitePipeline[];
  value: string;
  onChange: (pipelineSlug: string) => void;
  isLoading: boolean;
  disabled?: boolean;
  placeholder?: string;
}

interface PipelineResultsListProps {
  pipelines: BuildkitePipeline[];
  selectedPipelineSlug: string;
  onSelect: (pipeline: BuildkitePipeline) => void;
}

const PipelineResultsList = React.memo<PipelineResultsListProps>(function PipelineResultsList({
  pipelines,
  selectedPipelineSlug,
  onSelect,
}) {
  if (pipelines.length === 0) {
    return (
      <div className="p-3 text-xs text-muted-foreground">
        No pipelines found matching your search.
      </div>
    );
  }

  return (
    <div className="p-0.5">
      {pipelines.map((pipeline) => (
        <button
          key={pipeline.slug}
          onClick={() => onSelect(pipeline)}
          className={`
            w-full flex items-center justify-between px-2 py-1.5 text-xs rounded
            hover:bg-blue-950/40 transition-colors text-left
            ${selectedPipelineSlug === pipeline.slug ? "bg-card" : ""}
          `}
        >
          <div className="flex items-center gap-1.5 flex-1 min-w-0">
            <span className="truncate text-foreground">{pipeline.name}</span>
            <span className="text-xs text-muted-foreground shrink-0">({pipeline.slug})</span>
          </div>
          {selectedPipelineSlug === pipeline.slug && (
            <Check className="h-3 w-3 text-primary shrink-0" />
          )}
        </button>
      ))}
    </div>
  );
});

export function BuildkitePipelineTypeahead({
  pipelines,
  value,
  onChange,
  isLoading,
  disabled = false,
  placeholder = "Select a pipeline...",
}: BuildkitePipelineTypeaheadProps) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [triggerWidth, setTriggerWidth] = useState(0);
  const [isTyping, setIsTyping] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const filteredPipelines = useMemo(() => {
    if (!searchQuery) {
      // If no search query, show selected pipeline first if it exists
      if (value) {
        const selected = pipelines.find((p) => p.slug === value);
        if (selected) {
          const others = pipelines.filter((p) => p.slug !== value);
          return [selected, ...others.sort((a, b) => a.name.localeCompare(b.name))];
        }
      }
      return pipelines.slice().sort((a, b) => a.name.localeCompare(b.name));
    }

    const query = searchQuery.toLowerCase();

    // Filter pipelines that contain the query
    const matchingPipelines = pipelines.filter((p) =>
      p.name.toLowerCase().includes(query) || p.slug.toLowerCase().includes(query)
    );

    // Sort by edit distance
    return matchingPipelines.sort((a, b) => {
      const distanceA = levenshteinDistance(query, a.name.toLowerCase());
      const distanceB = levenshteinDistance(query, b.name.toLowerCase());

      if (distanceA === distanceB) {
        return a.name.localeCompare(b.name);
      }

      return distanceA - distanceB;
    });
  }, [pipelines, searchQuery, value]);

  // Show top 10 results
  const limitedPipelines = useMemo(() => filteredPipelines.slice(0, 10), [filteredPipelines]);

  const selectedPipeline = pipelines.find((p) => p.slug === value);

  useEffect(() => {
    if (triggerRef.current) {
      setTriggerWidth(triggerRef.current.offsetWidth);
    }
  }, []);

  const handleSelect = (pipeline: BuildkitePipeline) => {
    onChange(pipeline.slug);
    setOpen(false);
    setSearchQuery("");
    setIsTyping(false);
  };

  const handleButtonClick = () => {
    if (disabled || isLoading) return;
    if (!isTyping) {
      setIsTyping(true);
      setOpen(true);
      setTimeout(() => {
        inputRef.current?.focus();
      }, 50);
    }
  };

  // Handle popover close
  useEffect(() => {
    if (!open) {
      setIsTyping(false);
      setSearchQuery("");
    }
  }, [open]);

  return (
    <Popover open={open} onOpenChange={setOpen} modal={false}>
      <PopoverTrigger asChild>
        <div
          ref={triggerRef}
          onClick={handleButtonClick}
          className={`flex h-9 w-full items-center justify-between rounded-md border border-border bg-transparent px-3 py-2 text-sm cursor-pointer hover:bg-accent transition-colors ${
            disabled || isLoading ? "opacity-50 cursor-not-allowed" : ""
          }`}
        >
          {isTyping ? (
            <>
              <Input
                ref={inputRef}
                placeholder="Search pipelines..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="bg-transparent border-0 h-auto p-0 focus-visible:ring-0 focus-visible:ring-offset-0"
              />
              <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </>
          ) : (
            <>
              <span className="truncate">
                {isLoading ? (
                  <span className="flex items-center gap-2 text-muted-foreground">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Loading...
                  </span>
                ) : selectedPipeline ? (
                  <span className="text-foreground">{selectedPipeline.name}</span>
                ) : (
                  <span className="text-muted-foreground">{placeholder}</span>
                )}
              </span>
              <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </>
          )}
        </div>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 bg-popover border-border"
        side="bottom"
        align="start"
        sideOffset={0}
        style={{
          width: triggerWidth > 0 ? `${triggerWidth}px` : "100%",
        }}
        onOpenAutoFocus={(e) => {
          e.preventDefault();
        }}
      >
        {isLoading ? (
          <div className="p-3 text-xs text-muted-foreground flex items-center gap-2">
            <Loader2 className="h-3 w-3 animate-spin" />
            Loading pipelines...
          </div>
        ) : (
          <div
            className="max-h-[300px] overflow-y-scroll scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-gray-800"
            onWheel={(e) => {
              const container = e.currentTarget;
              const delta = e.deltaY;
              container.scrollTop += delta;
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <PipelineResultsList
              pipelines={limitedPipelines}
              selectedPipelineSlug={value}
              onSelect={handleSelect}
            />
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
